import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { useAuth } from '../contexts/AuthContext';
import { redactSecretsFromProperties } from '../utils/redactUrlSecrets';

interface AnalyticsContextType {
  trackEvent: (event: string, properties?: Record<string, unknown>) => void;
  identify: (userId: string, traits?: Record<string, unknown>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
  trackEvent: () => {},
  identify: () => {},
});

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const initialized = useRef(false);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
    const host = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';

    if (!apiKey || initialized.current) return;

    posthog.init(apiKey, {
      api_host: host,
      // `capture_pageview` manda a `$current_url` INTEIRA. O login por SSO da
      // Microsoft aterrissa em `/auth/microsoft/callback?access_token=<JWT>`,
      // entao sem o saneador abaixo o token de todo usuario que entra pela
      // Microsoft era gravado no PostHog, atrelado ao `distinct_id` dele.
      // O saneador roda na borda de SAIDA do proprio PostHog — nao depende de
      // qual efeito do React rodou primeiro.
      sanitize_properties: redactSecretsFromProperties,
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: false,
      session_recording: {
        maskAllInputs: true,
      },
      persistence: 'localStorage',
      loaded: () => {
        if (import.meta.env.DEV) {
          posthog.opt_out_capturing();
        }
      },
    });
    initialized.current = true;
  }, []);

  // Identify user on login, reset on logout
  useEffect(() => {
    if (!initialized.current) return;
    if (user) {
      posthog.identify(String(user.id), {
        role: user.role,
        tenantId: user.tenantId,
      });
      if (user.tenantId) {
        posthog.group('tenant', user.tenantId, {
          plan: (user as unknown as Record<string, unknown>).plan ?? undefined,
        });
      }
    } else {
      posthog.reset();
    }
  }, [user]);

  const trackEvent = useCallback((event: string, properties?: Record<string, unknown>) => {
    try {
      if (initialized.current) {
        posthog.capture(event, properties);
      }
    } catch {
      // fire-and-forget
    }
  }, []);

  const identify = useCallback((userId: string, traits?: Record<string, unknown>) => {
    try {
      if (initialized.current) {
        posthog.identify(userId, traits);
      }
    } catch {
      // fire-and-forget
    }
  }, []);

  return (
    <AnalyticsContext.Provider value={{ trackEvent, identify }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  return useContext(AnalyticsContext);
}
